<template>
  <div class="card">
    <h3 class="title">{{ name }}</h3>
    <div class="meta">
      <div class="data">
        <div class="icon"><i class="fa-solid fa-map"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.distancia") }}</div>
          <div class="data-value">{{ distanceKm.toFixed(1) }} km</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-bolt"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.potencia") }}</div>
          <div class="data-value">{{ powerKw }} kW</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-euro-sign"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.precio_kwh") }}</div>
          <div class="data-value">{{ priceKwh.toFixed(2) }} €</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-clock"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.tiempo_carga") }}</div>
          <div class="data-value">{{ chargeTimeMin }} min</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-receipt"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.coste_total") }}</div>
          <div class="data-value">{{ totalCost.toFixed(2) }} €</div>
        </div>
      </div>
      <div class="data">
        <div class="icon"><i class="fa-solid fa-circle" :class="statusClass"></i></div>
        <div class="data-content">
          <div class="data-title">{{ $t("components.chargerlistitem.estado") }}</div>
          <div class="data-value" :class="statusClass">
            {{ estadoTexto }}
          </div>
        </div>
      </div>
      <div class="action action-full-width">
        <button class="btn" @click="navigateToCharger">{{ $t("components.chargerlistitem.ir_al_cargador") }}</button>
      </div>
    </div>

    <!-- Navegación para cargadores agrupados -->
    <div v-if="mostrarNavegacionAgrupada" class="group-navigation">
      <div class="navigation-controls">
        <button 
          class="nav-button" 
          @click="$emit('navigate-previous')"
          :disabled="mostrarBotonesNavegacionAgrupada">
          <i class="fa-solid fa-chevron-left"></i>
        </button>
        <span class="counter">{{ (currentIndex ?? 0) + 1 }} {{ $t("components.chargerlistitem.de") }} {{ groupedCount }}</span>
        <button 
          class="nav-button" 
          @click="$emit('navigate-next')"
          :disabled="mostrarBotonesNavegacionAgrupada">
          <i class="fa-solid fa-chevron-right"></i>
        </button>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Mostrar el idioma detectado por el navegador en la consola
onMounted(() => {
  console.log('Idioma detectado por el navegador:', navigator.language);
  console.log('Idiomas preferidos del navegador:', navigator.languages);
});

const props = defineProps<{
  name: string;
  distanceKm: number;
  priceKwh: number;
  totalCost: number;
  powerKw: number;
  chargeTimeMin: number;
  latitud?: number;
  longitud?: number;
  Libre: number;
  Total: number;
  groupedCount?: number;
  currentIndex?: number;
}>();

const emit = defineEmits<{
  'navigate-next': [];
  'navigate-previous': [];
}>();

const mostrarNavegacionAgrupada = computed(() => props.groupedCount != null && props.groupedCount > 1);
const mostrarBotonesNavegacionAgrupada = computed(() => props.groupedCount != null && props.groupedCount <= 1);

// Calcular el texto y clase de estado según la nueva lógica
const estadoTexto = computed(() => {
  const { Libre, Total } = props;
  
  // Si Total = 1, el Estado es lo que indique Libre
  if (Total === 1) {
    return Libre === 1 ? t("components.chargerlistitem.libre") : t("components.chargerlistitem.ocupado");
  }
  
  // Si Total > 1
  if (Total > 1) {
    // Si Libre es igual a Total --> Estado es Libre, mostrar "Total libres"
    if (Libre === Total) {
      return `${Total} ${t("components.chargerlistitem.libres")}`;
    }
    // Si Libre es igual a cero --> Estado es Ocupado, mostrar "Total ocupadas"
    if (Libre === 0) {
      return `${Total} ${t("components.chargerlistitem.ocupadas")}`;
    }
    // Caso por defecto --> Estado es "Libre"(s) de "Total"
    return `${Libre} ${Libre === 1 ? t("components.chargerlistitem.libre_singular") : t("components.chargerlistitem.libres")} ${t("components.chargerlistitem.de")} ${Total}`;
  }
  
  // Fallback por si Total es 0 o undefined
  return Libre > 0 ? t("components.chargerlistitem.libre") : t("components.chargerlistitem.ocupado");
});

const statusClass = computed(() => {
  const { Libre, Total } = props;
  
  // Determinar si está libre o ocupado para el estilo
  let isLibre = false;
  
  if (Total === 1) {
    isLibre = Libre === 1;
  } else if (Total > 1) {
    // Si hay alguna toma libre, se considera parcialmente libre
    isLibre = Libre > 0;
  } else {
    isLibre = Libre > 0;
  }
  
  return isLibre ? 'status-libre' : 'status-ocupado';
});

function navigateToCharger() {
  if (!props.latitud || !props.longitud) {
    console.warn('No hay coordenadas disponibles para navegar');
    return;
  }

  const lat = props.latitud;
  const lon = props.longitud;

  // Google Maps URL universal que funciona en todos los dispositivos y navegadores
  // En dispositivos móviles, si hay apps de navegación instaladas (Google Maps, Waze, etc.),
  // el sistema operativo puede ofrecer abrirlas automáticamente
  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
  
  // Detectar si estamos en un dispositivo móvil
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    // En móviles, usar esquemas nativos que permiten seleccionar app
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    
    if (isAndroid) {
      // En Android, el esquema geo: permite al sistema proponer apps disponibles
      // Si no hay apps o falla, el navegador manejará el fallback automáticamente
      try {
        const geoUrl = `geo:${lat},${lon}?q=${lat},${lon}`;
        window.location.href = geoUrl;
      } catch {
        // Si geo: falla, usar Google Maps web como fallback
        window.location.href = googleMapsUrl;
      }
    } else if (isIOS) {
      // En iOS, usar el esquema maps: que permite al sistema proponer apps disponibles
      // (Apple Maps, Google Maps, Waze, etc.)
      // Si no hay apps o falla, el navegador manejará el fallback automáticamente
      try {
        const mapsUrl = `maps:?q=${lat},${lon}`;
        window.location.href = mapsUrl;
      } catch {
        // Si maps: falla, usar Google Maps web como fallback
        window.location.href = googleMapsUrl;
      }
    } else {
      // Otros móviles: usar Google Maps directamente
      window.location.href = googleMapsUrl;
    }
  } else {
    // Desktop: abrir Google Maps en nueva pestaña
    window.open(googleMapsUrl, '_blank');
  }
}
</script>

<style scoped lang="scss">
.card {
  border-radius: var(--card-border-radius, 16px);
  border: 1px solid var(--border-default-default, #C2CDDD);
  background: var(--background-default-default, #FFF);
  box-shadow: 0 3px 10px 0 rgba(14, 20, 26, 0.10);
  padding: var(--spacing-sm) var(--card-horizontal-padding, 16px);
}

.title {
  color: var(--text-primary-default, #D3135A);
  text-align: center;
  font-size: var(--font-size-body-lg, 18px);
  font-weight: 700;
  line-height: var(--line-height-body-lg, 32px);
}

.meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xs, 8px) var(--spacing-md, 16px);

}

.data {
  display: flex;
  gap: var(--spacing-xs, 8px);

  .icon {
    font-size: var(--font-size-body-md, 18px);
    text-align: center;
    color: var(--text-default-caption, #667790);
    min-width: var(--icon-size-md, 18px);
  }

  .data-content {
    display: flex;
    flex-direction: column;

    .data-title {
      color: var(--text-default-caption, #667790);
      font-size: var(--font-size-body-sm, 14px);
      font-weight: 900;
      line-height: var(--line-height-body-sm, 18px);
    }

    .data-value {
      color: var(--text-default-default, #000);
      font-size: var(--font-size-body-md, 18px);
      line-height: var(--line-height-body-md, 24px);
    }
  }
}


.action {
  display: flex;
  justify-content: flex-end;
  align-items: end;

  .btn {
    border-radius: var(--button-radius, 16px);
    border: 1px solid var(--border-secondary-default, #1863ED);
    background: var(--background-secondary-default, #1863ED);
    text-transform: none;
    color: var(--text-secondary-on-action, #FFF);
    font-size: var(--font-size-body-md, 18px);
    font-weight: 400;
  }
}

.action-full-width {
  grid-column: 1 / -1;
  justify-content: center;
  margin-top: var(--spacing-xs, 8px);
}

.status-libre {
  color: #22c55e;
}

.status-ocupado {
  color: #ef4444;
}

.group-navigation {
  margin-top: var(--spacing-sm, 12px);
  padding-top: var(--spacing-sm, 12px);
  border-top: 1px solid var(--border-default-default, #C2CDDD);
  display: flex;
  justify-content: flex-end;
}

.navigation-controls {
  display: flex;
  align-items: center;
  gap: var(--spacing-md, 16px);
}

.nav-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--border-default-default, #C2CDDD);
  background: var(--background-default-default, #FFF);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-default-default, #000);
  
  &:hover:not(:disabled) {
    background: var(--background-normal-hover, #F0F0F0);
    border-color: var(--border-secondary-default, #1863ED);
    color: var(--text-secondary-default, #1863ED);
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  i {
    font-size: 14px;
  }
}

.counter {
  color: var(--text-default-caption, #667790);
  font-size: var(--font-size-body-sm, 14px);
  font-weight: 600;
  min-width: 60px;
  text-align: center;
}
</style>
